import { deleteToken, getMessaging, getToken, onMessage } from 'firebase/messaging'
import { showNotify } from 'vant'
import Cookies from 'js-cookie'
import { pushToken, pushTokenOff } from '@/api/index.js'
import { firebaseVapidKey, isFirebaseConfigured } from '@core/config/firebase.js'
import { getFirebaseApp } from '@core/utils/firebase.js'
import CStorage from '@core/utils/storage.js'
import { isAndroid, isFacebookInAppBrowser, isIOS, isPwa } from '@core/utils/pwa.js'

const FCM_TOKEN_KEY = 'fcmToken'
const FCM_SW_PATH = `${import.meta.env.BASE_URL || '/'}sw-firebase-messaging.js`.replace(/([^:]\/)\/+/g, '$1')
const FCM_SW_SCOPE = `${import.meta.env.BASE_URL || '/'}firebase-cloud-messaging-push-scope/`.replace(/([^:]\/)\/+/g, '$1')
const APP_NAME = import.meta.env.VITE_APP_NAME || 'Game Lobby'
const DEFAULT_ICON = '/pwa/icons/256.png'
const PUSH_NOTIFY_CLASS = 'push-message-notify'
const SW_READY_TIMEOUT_MS = isAndroid ? 30000 : 15000
const GET_TOKEN_TIMEOUT_MS = isAndroid ? 60000 : (isIOS ? 30000 : 15000)
const ANDROID_GET_TOKEN_DELAY_MS = 1500
const ANDROID_PUSH_RETRY_DELAYS = [2000, 4000, 8000]
const DESKTOP_PUSH_RETRY_DELAYS = [1000]
const PUSH_VISIBILITY_SYNC_THROTTLE_MS = 5 * 60 * 1000

function shouldUseDedicatedFcmSw() {
  if (isIOS) return true
  // 部分 Android 浏览器关闭标签后，窄 scope 独立 FCM SW 会被 OEM 回收；
  // 改由 Workbox 主 SW（scope=/，已注入 FCM 脚本）承接推送订阅，存活率更高。
  if (isAndroid && !isPwa) return false
  return isAndroid
}

function parsePushPayload(payload = {}) {
  const notification = payload.notification || {}
  const data = payload.data || {}
  const fcmLink = payload.fcmOptions?.link

  return {
    title: notification.title || data.title || APP_NAME,
    body: notification.body || data.body || data.message || data.content || '',
    icon: notification.icon || data.icon || DEFAULT_ICON,
    url: data.url || data.click_action || notification.click_action || fcmLink || '/',
    tag: data.tag,
  }
}

let pushRouter = null
let pushNavigateListenerBound = false

export function bindPushRouter(router) {
  pushRouter = router
  initPushNavigateListener()
}

function navigateToPushUrl(rawUrl) {
  if (!rawUrl) return

  try {
    const parsed = new URL(rawUrl, window.location.origin)
    if (parsed.origin !== window.location.origin) {
      window.location.href = parsed.href
      return
    }

    const routePath = parsed.hash
      ? parsed.hash.replace(/^#/, '') || '/'
      : `${parsed.pathname}${parsed.search}`

    if (pushRouter) {
      void pushRouter.push(routePath || '/')
      return
    }

    window.location.href = parsed.href
  } catch {
    const routePath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`
    if (pushRouter) {
      void pushRouter.push(routePath)
      return
    }
    window.location.href = rawUrl
  }
}

function initPushNavigateListener() {
  if (!('serviceWorker' in navigator) || pushNavigateListenerBound) return
  pushNavigateListenerBound = true

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'PUSH_NAVIGATE') {
      navigateToPushUrl(event.data.url)
      return
    }

    if (event.data?.type === 'PUSH_TOKEN_REFRESHED' && event.data.token) {
      console.info('[Push] SW subscription refreshed, syncing token')
      CStorage.setData(FCM_TOKEN_KEY, event.data.token, { action: 'local' })
      void saveTokenToServer(event.data.token)
    }
  })
}

function showInAppPushMessage({ title, body }) {
  showNotify({
    type: 'primary',
    message: body ? `${title}\n${body}` : title,
    duration: 5000,
    className: PUSH_NOTIFY_CLASS,
  })
}

async function getFcmServiceWorkerRegistration() {
  if (shouldUseDedicatedFcmSw()) {
    return cachedSwRegistration || warmUpServiceWorkerRegistration()
  }
  return navigator.serviceWorker.ready
}

function buildNotificationOptions({ body, icon, url, tag }) {
  return {
    body,
    icon,
    badge: '/pwa/icons/96.png',
    data: { url },
    tag: tag || `push-${Date.now()}`,
    renotify: true,
  }
}

async function showNotificationViaSwMessage(title, options) {
  try {
    const registration = await getFcmServiceWorkerRegistration()
    const worker = registration?.active
    if (!worker) return false

    return await new Promise((resolve) => {
      const channel = new MessageChannel()
      let settled = false
      const finish = (ok) => {
        if (settled) return
        settled = true
        resolve(ok)
      }

      channel.port1.onmessage = (event) => {
        finish(event.data?.ok === true)
      }

      worker.postMessage({
        type: 'SHOW_NOTIFICATION',
        payload: { title, options },
      }, [channel.port2])

      setTimeout(() => finish(false), 3000)
    })
  } catch (error) {
    console.warn('[Push] postMessage to FCM SW failed', error)
    return false
  }
}

async function showSystemNotificationViaSw({ title, body, icon, url, tag }) {
  if (Notification.permission !== 'granted') return false

  const options = buildNotificationOptions({ body, icon, url, tag })

  // Android/iOS 前台：页面侧 showNotification 常无效，改由 FCM SW 展示
  if (shouldUseDedicatedFcmSw()) {
    const viaSwMessage = await showNotificationViaSwMessage(title, options)
    if (viaSwMessage) return true
  }

  try {
    const registration = await getFcmServiceWorkerRegistration().catch(() => null)
      || await navigator.serviceWorker.ready
    await registration.showNotification(title, options)
    return true
  } catch (error) {
    console.warn('[Push] registration.showNotification failed', error)
  }

  try {
    if (typeof Notification !== 'undefined') {
      new Notification(title, options)
      return true
    }
  } catch (error) {
    console.warn('[Push] new Notification failed', error)
  }

  return false
}

let messagingInstance = null
let foregroundListenerBound = false
let subscribeInFlight = null
let interactionListenerBound = false
let subscribeGestureHandled = false
let cachedSwRegistration = null
let swRegistrationPromise = null
let syncPushTokenInFlight = null
let lastVisibilityPushSyncAt = 0
let visibilitySyncBound = false
const watchedSwRegistrations = new WeakSet()

async function withTimeout(promise, timeoutMs, label) {
  let timer
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error(`${label} timeout (${timeoutMs}ms)`))
        }, timeoutMs)
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

function isLoggedIn() {
  return Boolean(Cookies.get('token') && Cookies.get('userid'))
}

function shouldSkipAutoSubscribe() {
  if (!isPushSupported()) return true
  if (!isLoggedIn()) return true
  if (Notification.permission === 'denied') return true
  if (!firebaseVapidKey) return true
  return false
}

function resetSubscribeInteraction() {
  subscribeGestureHandled = false
  interactionListenerBound = false
}

function bindSubscribeOnFirstInteraction() {
  if (interactionListenerBound || Notification.permission !== 'default') return
  interactionListenerBound = true

  console.info('[Push] 等待用户点击后再请求通知权限, permission:', Notification.permission)

  const handler = () => {
    if (subscribeGestureHandled) return
    subscribeGestureHandled = true
    document.removeEventListener('click', handler, true)
    document.removeEventListener('touchstart', handler, true)

    void autoSubscribePush({ fromUserGesture: true }).then((result) => {
      if (!result.ok && Notification.permission === 'default') {
        console.info('[Push] 订阅未完成，等待下次点击重试:', result.reason)
        resetSubscribeInteraction()
        bindSubscribeOnFirstInteraction()
      }
    })
  }

  document.addEventListener('click', handler, { capture: true })
  document.addEventListener('touchstart', handler, { capture: true, passive: true })
}

function getMessagingInstance() {
  if (!messagingInstance) {
    messagingInstance = getMessaging(getFirebaseApp())
  }
  return messagingInstance
}

export function isPushSupported() {
  return (
    isFirebaseConfigured()
    && 'serviceWorker' in navigator
    && 'Notification' in window
    && !isFacebookInAppBrowser()
  )
}

export function getPushUnsupportedReason() {
  if (!isFirebaseConfigured()) return 'not_configured'
  if (isFacebookInAppBrowser()) return 'in_app_browser'
  if (isIOS && !isPwa) return 'ios_need_pwa'
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return 'unsupported'
  return null
}

function getOsInfo() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera
  let agent = 'pc'

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    agent = 'iOS'
  } else if (/android/i.test(userAgent)) {
    agent = 'Android'
  }

  let platform = 'web'
  if (isPwa) platform = 'pwa'

  return `${agent}_${platform}`
}

function getCurrentUserId() {
  return Cookies.get('userid') || ''
}

function logPushToken(token) {
  if (!token) return
  console.info('[Push] FCM Token:', token)
}

function getCachedFcmToken() {
  return CStorage.getData(FCM_TOKEN_KEY, { action: 'local' }) || ''
}

function isPushServiceError(error) {
  const message = String(error?.message || error || '')
  return (
    error?.name === 'AbortError'
    || message.includes('push service error')
    || message.includes('Registration failed')
  )
}

function logPushServiceError(error) {
  console.error('[Push] push service registration failed:', error)

  if (isIOS) {
    console.error(
      '[Push] iOS 需确认 Firebase Console → 项目设置 → Cloud Messaging → '
      + 'Apple 应用配置 中已上传 APNs Authentication Key（.p8）',
    )
  } else if (isAndroid) {
    console.error(
      '[Push] Android push service error 排查：'
      + '1) 确认 Android 系统设置 → 应用 → Chrome/PWA → 通知 已开启；'
      + '2) chrome://gcm-internals Registration Log 为空 = GCM 存储损坏，需清除 Chrome 应用数据；'
      + '3) 重新部署后需卸载 PWA 重装。',
    )
  } else {
    console.error(
      '[Push] Desktop push service error 排查：'
      + '1) chrome://settings/content/notifications 确认站点通知已允许；'
      + '2) DevTools → Application → Service Workers → Unregister 后刷新；'
      + '3) 若仍失败，chrome://gcm-internals 查看是否有 TOO_MANY_REGISTRATIONS，'
      + '可新建 Chrome 用户配置文件或清除站点数据重试。',
    )
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function hasActivePushSubscription(registration) {
  if (!registration?.pushManager) return false
  try {
    const sub = await registration.pushManager.getSubscription()
    return Boolean(sub)
  } catch {
    return false
  }
}

async function logPushDiagnostics(registration) {
  const registrations = await navigator.serviceWorker.getRegistrations()
  const swList = registrations.map((reg) => ({
    scope: reg.scope,
    state: reg.active?.state || reg.installing?.state || reg.waiting?.state || 'none',
    scriptURL: reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL,
  }))

  let subscriptionInfo = null
  try {
    const sub = await registration.pushManager.getSubscription()
    subscriptionInfo = sub ? { endpoint: sub.endpoint.slice(0, 80) } : null
  } catch (error) {
    subscriptionInfo = { error: String(error?.message || error) }
  }

  console.info('[Push] diagnostics:', {
    permission: Notification.permission,
    isPwa,
    fcmScope: registration.scope,
    fcmState: registration.active?.state,
    fcmScript: registration.active?.scriptURL,
    subscription: subscriptionInfo,
    allServiceWorkers: swList,
  })
}

async function waitForRegistrationActive(registration) {
  if (registration.active?.state === 'activated') {
    return registration
  }

  const worker = registration.installing || registration.waiting
  if (!worker) {
    return registration
  }

  await withTimeout(
    new Promise((resolve, reject) => {
      if (worker.state === 'activated') {
        resolve()
        return
      }

      worker.addEventListener('statechange', () => {
        if (worker.state === 'activated') resolve()
        if (worker.state === 'redundant') {
          reject(new Error(`Service Worker became redundant: ${worker.state}`))
        }
      })
    }),
    SW_READY_TIMEOUT_MS,
    'serviceWorker.activate',
  )

  return registration
}

async function clearStalePushSubscription(registration) {
  try {
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return

    await subscription.unsubscribe()
    console.info('[Push] cleared stale push subscription:', registration.scope)
  } catch (error) {
    console.warn('[Push] clear push subscription failed', registration.scope, error)
  }
}

/** 清除所有 SW 上的旧 push 订阅，避免 Android GCM 注册累积 */
async function clearAllPushSubscriptions() {
  if (!('serviceWorker' in navigator)) return

  const registrations = await navigator.serviceWorker.getRegistrations()
  await Promise.all(registrations.map((reg) => clearStalePushSubscription(reg)))
}

async function migrateAndroidWebPushRegistration() {
  if (!isAndroid || isPwa || shouldUseDedicatedFcmSw()) return

  try {
    const legacyRegistration = await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE)
    if (!legacyRegistration) return

    console.info('[Push] migrating Android web push from dedicated FCM SW to Workbox SW')
    await clearStalePushSubscription(legacyRegistration)
    await legacyRegistration.unregister()
    cachedSwRegistration = null
    swRegistrationPromise = null
    CStorage.setData(FCM_TOKEN_KEY, '', { action: 'local' })
  } catch (error) {
    console.warn('[Push] migrate Android web push registration failed', error)
  }
}

async function getTokenServiceWorkerRegistration() {
  if (shouldUseDedicatedFcmSw()) {
    return getServiceWorkerRegistration()
  }
  // Windows/Mac：沿用 Workbox 主 SW 注册 token（原先可用的方案）
  return navigator.serviceWorker.ready
}

async function getServiceWorkerRegistration(options = {}) {
  const { forceRenew = false } = options
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker unavailable')
  }

  if (forceRenew) {
    const existing = await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE)
    if (existing) {
      console.info('[Push] unregistering FCM SW for renew:', FCM_SW_SCOPE)
      await existing.unregister()
      cachedSwRegistration = null
      swRegistrationPromise = null
      await sleep(500)
    }
  }

  let registration = await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE)

  if (!registration) {
    console.info('[Push] registering FCM SW:', FCM_SW_PATH, 'scope:', FCM_SW_SCOPE)
    registration = await navigator.serviceWorker.register(FCM_SW_PATH, {
      scope: FCM_SW_SCOPE,
      updateViaCache: 'none',
    })
  } else if (forceRenew) {
    await registration.update()
  }

  if (!registration.pushManager) {
    throw new Error('PushManager unavailable')
  }

  cachedSwRegistration = registration
  return waitForRegistrationActive(registration)
}

function warmUpServiceWorkerRegistration() {
  if (cachedSwRegistration) {
    return Promise.resolve(cachedSwRegistration)
  }

  if (!swRegistrationPromise) {
    swRegistrationPromise = getServiceWorkerRegistration()
      .then((registration) => {
        console.info('[Push] SW registration cached:', registration.active?.scriptURL)
        return registration
      })
      .catch((error) => {
        swRegistrationPromise = null
        throw error
      })
  }

  return swRegistrationPromise
}

async function fetchFcmToken(registration, options = {}) {
  const { retryCount = 0 } = options
  let activeRegistration = registration
    ? await waitForRegistrationActive(registration)
    : await getTokenServiceWorkerRegistration()

  const retryDelays = shouldUseDedicatedFcmSw()
    ? ANDROID_PUSH_RETRY_DELAYS
    : DESKTOP_PUSH_RETRY_DELAYS
  const maxRetries = retryDelays.length

  if (isAndroid && retryCount > 0) {
    activeRegistration = await getServiceWorkerRegistration({ forceRenew: true })
    await clearAllPushSubscriptions()
  }

  if (isAndroid && retryCount === 0) {
    await migrateAndroidWebPushRegistration()
    await sleep(ANDROID_GET_TOKEN_DELAY_MS)
  }

  await logPushDiagnostics(activeRegistration)

  console.info('[Push] calling getToken...', {
    sw: activeRegistration.active?.scriptURL,
    swState: activeRegistration.active?.state,
    scope: activeRegistration.scope,
    permission: Notification.permission,
    retryCount,
    mode: shouldUseDedicatedFcmSw() ? 'dedicated-fcm-sw' : 'workbox-sw',
    hasSubscription: await hasActivePushSubscription(activeRegistration),
  })

  try {
    const token = await withTimeout(
      getToken(getMessagingInstance(), {
        vapidKey: firebaseVapidKey,
        serviceWorkerRegistration: activeRegistration,
      }),
      GET_TOKEN_TIMEOUT_MS,
      'getToken',
    )

    const hasSubscription = await hasActivePushSubscription(activeRegistration)
    if (token && !hasSubscription && shouldUseDedicatedFcmSw()) {
      console.warn('[Push] getToken 返回 token 但无 push 订阅，强制重新获取')
      await clearAllPushSubscriptions()
      CStorage.setData(FCM_TOKEN_KEY, '', { action: 'local' })
      if (retryCount < maxRetries) {
        await sleep(retryDelays[retryCount] || 2000)
        return fetchFcmToken(activeRegistration, { retryCount: retryCount + 1 })
      }
    }

    if (token) {
      CStorage.setData(FCM_TOKEN_KEY, token, { action: 'local' })
      console.info('[Push] FCM token resolved, subscription:', hasSubscription)
    }

    return token || ''
  } catch (error) {
    if (isPushServiceError(error) && retryCount < maxRetries) {
      const delay = retryDelays[retryCount] || 2000
      console.warn(`[Push] getToken push service error, retry ${retryCount + 1}/${maxRetries} after ${delay}ms`)
      if (shouldUseDedicatedFcmSw()) {
        await clearAllPushSubscriptions()
      }
      await sleep(delay)
      return fetchFcmToken(null, { retryCount: retryCount + 1 })
    }

    await logPushDiagnostics(activeRegistration)
    logPushServiceError(error)
    throw error
  }
}

async function resolveFcmToken(registration, options = {}) {
  const { forceRefresh = false } = options
  if (!firebaseVapidKey) return ''

  const cached = getCachedFcmToken()
  const reg = registration || (shouldUseDedicatedFcmSw()
    ? (cachedSwRegistration || await warmUpServiceWorkerRegistration())
    : await navigator.serviceWorker.ready)

  if (cached && !forceRefresh) {
    const hasSubscription = await hasActivePushSubscription(reg)
    if (hasSubscription) {
      console.info('[Push] reuse cached FCM token, subscription active')
      return cached
    }
    console.warn('[Push] cached token 存在但 push 订阅已失效，重新获取')
  }

  try {
    return await fetchFcmToken(reg)
  } catch (error) {
    console.warn('[Push] getToken failed', error)
    const hasSubscription = await hasActivePushSubscription(reg).catch(() => false)
    if (cached && hasSubscription) {
      console.info('[Push] fallback to cached token with active subscription')
      return cached
    }
    CStorage.setData(FCM_TOKEN_KEY, '', { action: 'local' })
    return ''
  }
}

async function saveTokenToServer(token) {
  if (!token) {
    console.warn('[Push] 无可用 FCM Token，跳过 push_token')
    return
  }

  logPushToken(token)

  if (!getCurrentUserId()) return

  try {
    await pushToken({
      push_token: token,
      platform: isPwa ? 'pwa' : 'web',
      os_info: getOsInfo(),
    })
    CStorage.setData(FCM_TOKEN_KEY, token, { action: 'local' })
    console.info('[Push] push_token 已上报, userid:', getCurrentUserId())
  } catch (error) {
    console.warn('[Push] 上报 push_token 失败', error)
  }
}

function showForegroundNotification(payload) {
  const parsed = parsePushPayload(payload)
  console.info('[Push] foreground message', parsed, payload)

  // 页面在前台时 Chrome 常静默系统通知，必须展示站内 Toast
  if (document.visibilityState === 'visible') {
    showInAppPushMessage(parsed)
  }

  void showSystemNotificationViaSw(parsed)
}

export function initForegroundPush() {
  if (!isPushSupported() || foregroundListenerBound) return

  const bindListener = () => {
    if (foregroundListenerBound) return

    try {
      const messaging = getMessagingInstance()
      onMessage(messaging, (payload) => {
        showForegroundNotification(payload)
      })
      foregroundListenerBound = true
      console.info('[Push] foreground listener ready')
    } catch (error) {
      console.error('[Push] initForegroundPush failed', error)
    }
  }

  bindListener()

  if (!foregroundListenerBound && 'serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(bindListener)
      .catch((error) => {
        console.error('[Push] service worker not ready', error)
      })
  }
}

export async function subscribePush(options = {}) {
  const { fromUserGesture = false } = options
  const unsupportedReason = getPushUnsupportedReason()
  if (unsupportedReason) {
    console.warn('[Push] unsupported:', unsupportedReason)
    return { ok: false, reason: unsupportedReason }
  }

  if (!firebaseVapidKey) {
    console.warn('[Push] 请在 .env 中配置 VITE_FIREBASE_VAPID_KEY')
    return { ok: false, reason: 'no_vapid_key' }
  }

  if (Notification.permission === 'denied') {
    console.warn('[Push] 通知权限已被拒绝，需到系统设置中手动开启')
    return { ok: false, reason: 'denied' }
  }

  if (Notification.permission === 'default') {
    if (!fromUserGesture) {
      bindSubscribeOnFirstInteraction()
      return { ok: false, reason: 'need_user_gesture' }
    }

    // iOS 要求 requestPermission 在用户手势内立即调用，不能先 await SW
    console.info('[Push] requesting notification permission...')
    const permission = await Notification.requestPermission()
    console.info('[Push] notification permission:', permission)

    if (permission !== 'granted') {
      if (permission === 'default') {
        resetSubscribeInteraction()
      }
      return { ok: false, reason: 'denied' }
    }
  }

  let registration = null
  if (shouldUseDedicatedFcmSw()) {
    registration = cachedSwRegistration
    if (!registration) {
      try {
        registration = await warmUpServiceWorkerRegistration()
      } catch (error) {
        console.error('[Push] service worker not ready after permission', error)
        return { ok: false, reason: 'sw_not_ready', error }
      }
    }
  }

  try {
    const token = await fetchFcmToken(registration)

    if (!token) {
      return { ok: false, reason: 'no_token' }
    }

    await saveTokenToServer(token)
    return { ok: true, token }
  } catch (error) {
    console.error('[Push] subscribePush failed', error)
    return { ok: false, reason: 'error', error }
  }
}

function watchServiceWorkerRegistration(registration) {
  if (!registration || watchedSwRegistrations.has(registration)) return
  watchedSwRegistrations.add(registration)

  registration.addEventListener('updatefound', () => {
    const worker = registration.installing
    if (!worker) return

    worker.addEventListener('statechange', () => {
      if (worker.state !== 'activated') return
      console.info('[Push] sync on SW update:', registration.scope)
      void syncPushTokenOnLogin()
    })
  })
}

function bindPushSyncOnSwUpdate() {
  if (!('serviceWorker' in navigator)) return

  navigator.serviceWorker.ready
    .then(watchServiceWorkerRegistration)
    .catch((error) => {
      console.warn('[Push] watch main SW update failed', error)
    })

  if (shouldUseDedicatedFcmSw()) {
    warmUpServiceWorkerRegistration()
      .then(watchServiceWorkerRegistration)
      .catch((error) => {
        console.warn('[Push] watch FCM SW update failed', error)
      })
  }
}

function bindPushSyncOnVisibility() {
  if (visibilitySyncBound) return
  visibilitySyncBound = true

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    if (!isLoggedIn()) return

    const now = Date.now()
    if (now - lastVisibilityPushSyncAt < PUSH_VISIBILITY_SYNC_THROTTLE_MS) return
    lastVisibilityPushSyncAt = now

    console.info('[Push] sync on visibility')
    void syncPushTokenOnLogin({ forceRefresh: true })
  })
}

export async function syncPushTokenIfGranted(options = {}) {
  if (!isPushSupported()) return { ok: false, reason: getPushUnsupportedReason() }
  if (Notification.permission !== 'granted') return { ok: false, reason: 'not_granted' }
  if (!firebaseVapidKey) return { ok: false, reason: 'no_vapid_key' }

  try {
    const token = await resolveFcmToken(undefined, {
      forceRefresh: options.forceRefresh !== false,
    })

    if (!token) return { ok: false, reason: 'no_token' }

    await saveTokenToServer(token)
    return { ok: true, token }
  } catch (error) {
    console.error('[Push] syncPushTokenIfGranted failed', error)
    return { ok: false, reason: 'error', error }
  }
}

/** 登录成功后调用：已授权则立即 push_token，未授权则等待用户点击 */
export async function syncPushTokenOnLogin(options = {}) {
  if (syncPushTokenInFlight) return syncPushTokenInFlight

  syncPushTokenInFlight = (async () => {
    if (!isPushSupported()) {
      console.warn('[Push] skipped on login:', getPushUnsupportedReason())
      return { ok: false, reason: getPushUnsupportedReason() || 'skipped' }
    }

    if (!isLoggedIn()) {
      return { ok: false, reason: 'skipped' }
    }

    initForegroundPush()

    console.info('[Push] sync on login, permission:', Notification.permission)

    if (Notification.permission === 'granted') {
      return syncPushTokenIfGranted({
        forceRefresh: options.forceRefresh === true,
      })
    }

    if (Notification.permission === 'default') {
      bindSubscribeOnFirstInteraction()
      return { ok: false, reason: 'need_user_gesture' }
    }

    console.warn('[Push] 通知权限已被拒绝')
    return { ok: false, reason: 'denied' }
  })().finally(() => {
    syncPushTokenInFlight = null
  })

  return syncPushTokenInFlight
}

export async function unsubscribePush() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' }

  try {
    const cachedToken = CStorage.getData(FCM_TOKEN_KEY, { action: 'local' })
    const messaging = getMessagingInstance()
    await deleteToken(messaging)

    if (cachedToken) {
      await pushTokenOff({ push_token: cachedToken })
    }

    CStorage.setData(FCM_TOKEN_KEY, '', { action: 'local' })
    return { ok: true }
  } catch (error) {
    console.error('[Push] unsubscribePush failed', error)
    return { ok: false, reason: 'error', error }
  }
}

export async function autoSubscribePush(options = {}) {
  if (shouldSkipAutoSubscribe()) {
    return { ok: false, reason: 'skipped' }
  }

  if (Notification.permission === 'granted') {
    return syncPushTokenIfGranted()
  }

  if (Notification.permission === 'default' && !options.fromUserGesture) {
    bindSubscribeOnFirstInteraction()
    return { ok: false, reason: 'need_user_gesture' }
  }

  if (subscribeInFlight) return subscribeInFlight

  subscribeInFlight = subscribePush(options)
    .finally(() => {
      subscribeInFlight = null
    })

  return subscribeInFlight
}

export function initPushMessaging() {
  if (!isPushSupported()) return

  if (shouldUseDedicatedFcmSw()) {
    warmUpServiceWorkerRegistration().catch((error) => {
      console.warn('[Push] SW warm-up on init failed', error)
    })
  }
  initForegroundPush()
  bindPushSyncOnSwUpdate()
  bindPushSyncOnVisibility()
}
